/* ── 📐 M(장표) 카드 **배치 산식** SSOT — 렌더러와 검증이 같은 함수를 지난다 (2026-08-24 W1 R1) ──
 *
 * ★왜 렌더러에서 뺐나(적대 리뷰 correctness-P2)★: 「장표에 생동감」의 지목 글자 검증은 "카드 재료에
 *   그 글자가 있나"만 물었다. 그런데 **재료에 있다 ≠ 화면에 보인다** — `drawDataScene` 은 줄바꿈
 *   결과를 y 로 누적할 뿐 캔버스 높이를 넘는 줄을 잘라내지 않아, **넘친 줄은 프레임 밖에 그려진다**.
 *   16:9(1280×720)에서는 여섯 줄짜리 카드가 실제로 넘친다(줄당 66px 누적 · 높이 720). 화면 밖 글자를
 *   지목하면 모델은 그 글자를 **스스로 지어내 그린다** = 이 기능이 막으려던 바로 그 사고다.
 *   ⇒ 배치 산식을 순수 함수로 내리고, 검증이 **"실제로 그려지는 조각"** 만 haystack 으로 쓴다.
 *
 * ★★추출이지 개정이 아니다★★ — `drawDataScene` 의 좌표·누적·줄바꿈은 1비트도 바뀌지 않았다.
 *   그 함수는 이제 이 모듈이 계산한 조각을 순서대로 그리기만 한다(계약 테스트가 산식을 못박는다).
 *
 * ★폭 측정은 주입한다(`measureTitle`·`measureLine`)★ — 렌더러는 캔버스 `measureText` 를 주고,
 *   검증은 `estimateTextWidth`(폰트 메트릭 없는 보수 추정)를 준다. **캔버스를 못 쓰는 표면이 있기
 *   때문**이다(인스펙터는 측정용 캔버스를 띄우지 않고, jsdom 의 `getContext("2d")` 는 null 이다).
 *
 * ★★그 차이가 ★어느 방향★ 인지는 축마다 다르다 — 단정하지 않는다(2026-08-24 R2 · 적대 리뷰 P2)★★
 *   · **데이터 줄** — 렌더러가 쓰는 폰트는 `600 …px "Noto Sans KR"` 로 **고정**이고, 추정치는
 *     전각 1em·라틴 0.62em 로 그 폰트의 실제 전진폭보다 **넉넉**하다 ⇒ 추정 줄바꿈 ≥ 실제 줄바꿈 ⇒
 *     추정 y ≥ 실제 y ⇒ 이 축에서는 검증이 보는 조각이 실제의 부분집합인 방향이다.
 *   · **제목** — 렌더러는 `titleFontCss(activeTitleFontId, …)` 로 **유저가 고른 커스텀 폰트**를 쓴다.
 *     그 폰트가 라틴 폭 0.62em 보다 넓으면(넓은 세리프·W·M 연속 등) **실제 제목이 추정보다 더 접히고**
 *     그만큼 데이터 줄 전체가 아래로 밀린다 ⇒ 이 축에서는 추정 y < 실제 y 가 될 수 있고, 그러면
 *     **화면 밖으로 밀린 글자를 "보인다"고 통과**시킬 수 있다.
 * ⇒ 그래서 가시 판정에 **보수 여백(`visibilityMargin` = 데이터 줄 1줄 높이)** 을 둔다: 아랫변이
 *   그 여백 안에 걸치는 조각은 **비가시로 처분**한다(fail-closed 방향). ★이건 증명이 아니라 완충이다★
 *   — 제목이 한 줄 더 접히면 밀림(`titleSize × 1.2`)이 이 여백(`lineSize × 1.3`)보다 커서, 아주 넓은
 *   커스텀 제목 폰트에서는 여전히 창이 남는다. **완전히 닫으려면 검증도 실제 폰트 메트릭으로 재야
 *   하고, 그건 캔버스가 없는 표면에서 불가능하다**(그래서 남은 창을 여기 적어 둔다).
 * ★추정을 "절대 상한"이라 부르지 않는다★ — 폰트 메트릭을 모르므로 증명할 수 없다. 지향일 뿐이다.
 *
 * ⚠️ ★★이 여백은 ★상시 비용★ 이다 — 알고 남긴다(2026-08-24 R3 · 적대 리뷰 P3 · ★회부★)★★
 *   여백이 필요한 진짜 사정은 **커스텀 제목 폰트로 제목이 더 접혀 데이터 줄이 밀리는 경우** 하나인데,
 *   판정은 그 사정과 무관하게 **모든 카드**에 여백을 요구한다 ⇒ 기본 폰트 카드에서 **화면에 온전히
 *   보이는 맨 아랫줄까지 지목 대상에서 빠진다**(유저가 보고 있는 글자를 "없다"고 말하는 창).
 *   좁히려면 검증부(`visibleCardTexts`·인스펙터)가 **그 컷에 걸린 제목 폰트 id** 를 알아야 하는데
 *   지금 인자에 없다(굽기 호출만 안다) ⇒ **여기서 고치지 않고 회부한다**. 그때까지의 처분은
 *   fail-closed 쪽(과잉 거절 > 없는 글자 지목 = 금액 변조)이고, 사유 문구가 "화면 맨 아래 끝에
 *   걸린 글자"라고 그 사실을 ★미리 말한다★(`omni-slide-motion` 의 가시성 사유).
 */

/** 📐 합본 캔버스 크기 표 — ★정본★. `cinematicRender.canvasSizeFor` 가 이 함수를 위임 호출한다.
 *  ★표를 복제하지 않는다★(`cine-caption-plan.ts` 헤더가 못 박은 관용구): 렌더 번들(supabase·mediabunny
 *  를 무는 파일)에 표가 살면 비-렌더 표면(카드 배치 검증)이 그 표를 못 읽어 사본을 만들게 된다. */
export function canvasSizeForAspect(aspect: string): { width: number; height: number } {
  switch (aspect) {
    case "16:9":
      return { width: 1280, height: 720 };
    case "1:1":
      return { width: 1080, height: 1080 };
    case "4:3":
      return { width: 1280, height: 960 };
    case "3:4":
      return { width: 960, height: 1280 };
    case "4:5":
      return { width: 1080, height: 1350 };
    case "9:16":
    default:
      return { width: 720, height: 1280 };
  }
}

/* ── 🔠 M 카드 글자 **하한**(2026-08-24 · 장표 동결 모션 W1) ──────────────────────────────
 *
 * ★왜 하한이 필요한가(실측 · 이치죠 2026-08-23)★: 장표 PNG 를 Omni i2v 시작 프레임으로 주면
 *   ★첫 프레임 재렌더★ 가 원본을 IoU ~0.85 로 깨뜨리는데, 깨지는 축이 **"최소 크기 순한글 긴
 *   문장"** 이었다(같은 픽셀 크기라도 숫자·기호 위주 줄은 무사). 그 파괴는 **동결이 시작되기
 *   전에** 일어나므로 프롬프트로는 못 막는다 ⇒ 애초에 너무 작은 글자를 굽지 않는 것이 유일한 수단.
 * ★선례★: `lib/text-plate-layout.ts` 의 `TEXT_PLATE_MIN_FONT_PX = 14` + `textPlateFontPx` 클램프
 *   (같은 사정 — 판이 영상 모델을 한 번 더 통과하며 재압축된다).
 *
 * ★★값의 근거 — 이건 **데드레터 안전핀**이다(정직 고지)★★
 *   현 산식은 `titleSize = round(W × 0.058)` · `lineSize = round(W × 0.04)` 이고, `canvasSizeForAspect`
 *   가 주는 **최소 지원 캔버스 폭은 720**(9:16)이다. 그 폭에서 실제 계산값은
 *     · titleSize = round(720 × 0.058) = round(41.76) = **42**
 *     · lineSize  = round(720 × 0.04)  = round(28.8)  = **29**
 *   하한을 **바로 그 값**으로 잡았다 ⇒ 현재 지원하는 모든 비율(720·960·1080·1280 폭)에서 클램프가
 *   ★한 번도 발동하지 않는다★ = **기존 M 컷의 픽셀이 1비트도 안 바뀐다**(회귀 0). 이 하한이 하는
 *   일은 "앞으로 캔버스가 더 작아지거나 산식 계수가 낮아질 때 조용히 깨지는 글자가 생기는 것"을
 *   막는 것뿐이다. 하한을 실측 파괴 임계까지 **올리는** 것은 기존 컷의 룩을 바꾸는 일이라 별건이며,
 *   그 임계값 자체가 아직 없다(실측은 "작은 순한글이 깨진다"까지고 px 경계는 미측정).
 * ★한 곳 수리로 족하다★ — 카드 사진 굽기(`bakeMotionCardStill`)는 합본과 **같은 `drawDataScene`** 을
 *   t01=1 로 부르므로(사본 금지 계약), 여기 하한을 세우면 굽기·합본 두 룩이 함께 따라온다.
 */
export const MOTION_CARD_MIN_TITLE_PX = 42;
export const MOTION_CARD_MIN_LINE_PX = 29;

/** 📊 M 카드 제목 글자 크기(px) — 산식 + 하한. ★계약 테스트가 이 함수로 잰다★(산식 복제 금지). */
export function motionCardTitleSizePx(W: number): number {
  return Math.max(MOTION_CARD_MIN_TITLE_PX, Math.round(W * 0.058));
}

/** 📊 M 카드 데이터 줄 글자 크기(px) — 산식 + 하한(위와 같은 근거). */
export function motionCardLineSizePx(W: number): number {
  return Math.max(MOTION_CARD_MIN_LINE_PX, Math.round(W * 0.04));
}

/* ── ✂️ 줄바꿈 ─────────────────────────────────────────────────────────────── */

/**
 * ✂️ 폭 측정 함수를 받아 줄을 나눈다 — `mediabunnyRender.wrapLines` 의 산식 그대로다
 *   (한 글자씩 붙여 보고 넘치면 끊는다 · `\n` 은 먼저 가른다 · 글자를 버리지 않는다).
 * ★"글자를 버리지 않는다"가 계약이다★ — 검증이 조각들을 다시 이어 붙여 원문을 복원하기 때문이다.
 */
export function wrapByMeasure(measure: (text: string) => number, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const raw of String(text ?? "").split("\n")) {
    let cur = "";
    for (const ch of Array.from(raw)) {
      const test = cur + ch;
      if (measure(test) > maxW && cur) {
        out.push(cur);
        cur = ch;
      } else {
        cur = test;
      }
    }
    if (cur) out.push(cur);
  }
  return out;
}

/**
 * ✂️📝 **낱말 우선 줄나눔**(2026-09-16 · 정세호 QA «간 / 편 암 PASS» — 낱말 중간에서 끊기던 오프닝 카드 제목).
 *   띄어쓰기 자리에서 먼저 끊고, 한 낱말이 한 줄 폭보다 길 때만 그 낱말 안에서 글자 단위로 끊는다(`wrapByMeasure` 산식).
 * ★계약★: 공백이 아닌 글자는 하나도 버리지 않는다(줄 경계의 공백 1칸만 소비한다) · 줄 안 공백은 그대로 · `\n` 은 먼저 가른다.
 * ★자막(`motionCardLayout`)은 이 함수를 쓰지 않는다★ — 자막 줄나눔 검증이 조각을 이어 붙여 원문을 복원하기 때문이다.
 *   이 함수의 소비처는 타이틀 카드(`fitTitleLines`)뿐이다.
 */
export function wrapWordsByMeasure(measure: (text: string) => number, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const raw of String(text ?? "").split("\n")) {
    let cur = "";
    for (const word of raw.split(" ")) {
      if (word === "") { if (cur) cur += " "; continue; } // 줄 안 연속 공백은 보존
      const test = cur ? `${cur} ${word}` : word;
      if (measure(test) <= maxW) { cur = test; continue; }
      if (cur.trim()) out.push(cur.trimEnd()); // 띄어쓰기 자리에서 끊는다(경계 공백 1칸 소비)
      if (measure(word) <= maxW) { cur = word; continue; }
      const pieces = wrapByMeasure(measure, word, maxW); // 한 낱말이 한 줄보다 길다 — 그 안에서만 글자 단위
      out.push(...pieces.slice(0, -1));
      cur = pieces[pieces.length - 1] ?? "";
    }
    if (cur.trim()) out.push(cur.trimEnd());
  }
  return out;
}

/**
 * 🏷 타이틀 카드 제목 **줄 수 맞춤**(2026-09-14 · 삼성생명 편성 후속 ③) — 긴 시리즈명이 16:9 카드에서 4~5줄로
 *   쏟아지던 결함. 글자를 버리지 않고(줄임표 없음) 크기를 단계적으로 줄여 `maxLines` 안에 넣는다.
 * ★기본 크기에서 이미 들어가면 결과가 종전과 바이트 같다★ — 짧은 제목의 카드는 한 픽셀도 안 변한다.
 * ★하한(`minScale`)에서도 넘치면 그 크기로 넘치는 줄까지 다 그린다★ — 자르는 것보다 낫다(제목은 원문이다).
 * `measure` 는 «이 크기로 이 글자열의 폭» — 캔버스가 폰트를 바꿔 재고, 테스트는 전각 추정으로 잰다.
 * ★줄나눔은 낱말 우선(`wrapWordsByMeasure` · 2026-09-16)★ — 띄어쓰기 없는 제목은 종전 글자 단위와 같다.
 */
export function fitTitleLines(opts: {
  text: string;
  maxW: number;
  baseSize: number;
  maxLines: number;
  minScale?: number;
  step?: number;
  measure: (text: string, size: number) => number;
}): { size: number; lines: string[]; scale: number } {
  const minScale = opts.minScale ?? 0.5;
  const step = opts.step ?? 0.05;
  let last = { size: opts.baseSize, lines: [] as string[], scale: 1 };
  for (let scale = 1; scale >= minScale - 1e-9; scale = Math.round((scale - step) * 1000) / 1000) {
    const size = Math.max(1, Math.round(opts.baseSize * scale));
    const lines = wrapWordsByMeasure((t) => opts.measure(t, size), opts.text, opts.maxW);
    last = { size, lines, scale };
    if (lines.length <= opts.maxLines) return last;
  }
  return last;
}

/** 🏷 타이틀 카드가 허용하는 제목 줄 수 — 가로(16:9)는 높이가 얕아 3줄, 세로는 4줄. */
export function titleCardMaxLines(width: number, height: number): number {
  return width > height ? 3 : 4;
}

/** 🈳 이 글자가 전각(한글·CJK)인가 — 추정 폭의 유일한 분기. */
function isWideChar(code: number): boolean {
  return (
    (code >= 0x1100 && code <= 0x115f) || // 한글 자모
    (code >= 0x2e80 && code <= 0xa4cf) || // CJK 부수 ~ 이(Yi)
    (code >= 0xac00 && code <= 0xd7a3) || // 한글 음절
    (code >= 0xf900 && code <= 0xfaff) || // CJK 호환
    (code >= 0xfe30 && code <= 0xfe4f) ||
    (code >= 0xff00 && code <= 0xff60) || // 전각 형태
    (code >= 0xffe0 && code <= 0xffe6) ||
    code >= 0x20000
  );
}

/**
 * 📏 **폰트 메트릭 없이 재는 보수 추정 폭**(px) — 캔버스를 못 쓰는 표면(검증)이 쓴다.
 *   한글·CJK = 1em(Noto Sans KR 실제 전진폭과 사실상 같다) · 나머지 = 0.62em(라틴 평균보다 넉넉).
 * ★모듈 헤더의 "안전한 방향" 문단이 이 값의 계약이다★ — 넉넉하게 잡아 **더 일찍 넘치게** 만든다.
 */
export function estimateTextWidth(text: string, fontPx: number): number {
  let em = 0;
  for (const ch of String(text ?? "")) em += isWideChar(ch.codePointAt(0) ?? 0) ? 1 : 0.62;
  return em * fontPx;
}

/* ── 📐 배치 ───────────────────────────────────────────────────────────────── */

/** 카드에 그려지는 글자 조각 하나 — 줄바꿈된 뒤의 ★실제 한 줄★ 이다. */
export interface MotionCardPiece {
  kind: "title" | "line";
  /** 이 조각의 글자(줄바꿈 조각 · 원문에서 잘라 온 그대로 — 버려진 글자는 없다). */
  text: string;
  x: number;
  y: number;
  /** 이 조각의 글자 크기(px) — 가시 판정과 그리기가 ★같은 값★ 을 쓴다. */
  fontPx: number;
  /** 데이터 줄의 원본 인덱스(제목은 `-1`). 검증이 조각을 원문 단위로 다시 잇는 열쇠다. */
  sourceIndex: number;
  /** 이 줄이 화면에 나타난 정도(0~1). `drawDataScene` 의 alpha·slide-in 과 같은 값. */
  appear: number;
  /**
   * 캔버스 안에 ★온전히★ 들어가는가(윗변·아랫변 모두 · **보수 여백까지 포함**). 부분 노출은 `false`.
   * ★여백의 근거는 모듈 헤더의 "제목 축" 문단★ — 검증이 제목을 커스텀 폰트로 못 재기 때문이다.
   */
  onCanvas: boolean;
}

/** 데이터 줄 앞의 점(●) — 그리기 전용이라 가시 판정에 쓰지 않는다.
 *  ★`sourceIndex` 를 함께 든다★: 렌더러가 "이 점 다음에 오는 조각들"을 종전 `forEach` 와 같은
 *  순서·같은 alpha 로 그리려면 점과 조각을 원문 단위로 이을 열쇠가 필요하다. */
export interface MotionCardDot { x: number; y: number; r: number; appear: number; sourceIndex: number }

export interface MotionCardLayout {
  pad: number;
  titleSize: number;
  lineSize: number;
  /**
   * 🛡 가시 판정의 **보수 여백**(px) — `onCanvas` 가 요구하는 아랫변 여유. 데이터 줄 1줄 높이다.
   * ★그리기는 이 값을 읽지 않는다★ — `drawDataScene` 은 조각을 전부 그리므로 픽셀은 1비트도 안 바뀐다.
   *   이 값이 좁히는 것은 **"지목해도 되는 글자"의 모집단** 하나뿐이다(모듈 헤더의 제목 축 문단).
   * ★계약 테스트가 산식을 복제하지 않게 값으로 내보낸다★.
   */
  visibilityMargin: number;
  pieces: MotionCardPiece[];
  dots: MotionCardDot[];
}

export interface MotionCardLayoutInput {
  title: string;
  lines: readonly string[];
  W: number;
  H: number;
  /** 진행도 0~1 — 데이터 줄의 등장 시점과 slide-in 오프셋이 여기 달렸다(굽기는 언제나 1). */
  t01: number;
  /** 캐릭터 그림이 실리는가 — 오른쪽 여백(`W × 0.26`)을 예약해 줄바꿈 폭이 좁아진다. */
  hasCharacterImage: boolean;
  measureTitle: (text: string) => number;
  measureLine: (text: string) => number;
}

/**
 * 📐 카드 한 프레임의 배치 — `drawDataScene` 의 좌표·y 누적·줄바꿈을 그대로 옮긴 순수 계산.
 * ★`appear <= 0` 인 줄은 조각도 y 누적도 만들지 않는다★ — 원본 `forEach` 의 `return` 과 같다
 *   (여기가 어긋나면 애니메이션 중 줄 위치가 통째로 밀린다).
 */
export function motionCardLayout(input: MotionCardLayoutInput): MotionCardLayout {
  const { W, H, t01 } = input;
  const pad = Math.round(W * 0.06);
  const titleSize = motionCardTitleSizePx(W);
  const lineSize = motionCardLineSizePx(W);
  const pieces: MotionCardPiece[] = [];
  const dots: MotionCardDot[] = [];
  /** 🛡 보수 여백 = 데이터 줄 1줄 높이(`lineSize × 1.3` — y 누적 산식과 같은 계수). 근거=모듈 헤더. */
  const visibilityMargin = lineSize * 1.3;
  const fits = (y: number, fontPx: number) => y >= 0 && y + fontPx + visibilityMargin <= H;

  let y = pad + H * 0.02;
  for (const tl of wrapByMeasure(input.measureTitle, input.title || "", W - pad * 2)) {
    pieces.push({ kind: "title", text: tl, x: pad, y, fontPx: titleSize, sourceIndex: -1, appear: 1, onCanvas: fits(y, titleSize) });
    y += titleSize * 1.2;
  }
  y += titleSize * 0.5;

  const charReserveW = input.hasCharacterImage ? W * 0.26 : 0;
  input.lines.forEach((line, i) => {
    const appear = Math.min(1, Math.max(0, (t01 - (0.12 + i * 0.08)) / 0.12));
    if (appear <= 0) return;
    const dx = pad + (1 - appear) * (W * 0.03);
    dots.push({ x: dx + lineSize * 0.3, y: y + lineSize * 0.65, r: lineSize * 0.2, appear, sourceIndex: i });
    const tx = dx + lineSize;
    for (const wl of wrapByMeasure(input.measureLine, line, W - pad - tx - charReserveW)) {
      pieces.push({ kind: "line", text: wl, x: tx, y, fontPx: lineSize, sourceIndex: i, appear, onCanvas: fits(y, lineSize) });
      y += lineSize * 1.3;
    }
    y += lineSize * 0.5;
  });

  return { pad, titleSize, lineSize, visibilityMargin, pieces, dots };
}

/**
 * 👁 **캔버스 안에 실제로 그려지는 글자** — 원문 단위(제목 1개 + 데이터 줄 N개)로 다시 이어 준다.
 *
 * ★조각 단위로 돌려주면 안 된다★: 카드에 "월 이백육만 원까지 지원됩니다"가 두 줄로 접혔을 때
 *   유저가 그 구절을 지목하면 어느 조각에도 부분열로 없다 → 실재하는 글자를 거절하게 된다.
 *   그래서 **같은 원문에서 나온 on-canvas 조각을 순서대로 이어** 돌려준다(줄바꿈은 글자를
 *   버리지 않으므로 이어 붙이면 원문 접두사가 정확히 복원된다).
 * ★넘쳐서 잘린 꼬리는 빠진다★ — 그것이 이 함수의 존재 이유다(넘친 줄 = 프레임 밖 = 모델이 못 본다).
 */
export function motionCardVisibleTexts(layout: MotionCardLayout): string[] {
  const bySource = new Map<number, string>();
  for (const p of layout.pieces) {
    if (!p.onCanvas) continue;
    bySource.set(p.sourceIndex, (bySource.get(p.sourceIndex) ?? "") + p.text);
  }
  return [...bySource.values()].filter((s) => s.trim());
}
